import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ItemForm from "../components/ItemForm";
import "../App.css";

const BASE_URL = "https://ca2-team11-server-0rto.onrender.com";

export default function EditItem() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [values, setValues] = useState({
    name: "",
    image: "",
    material: "",
    quantity: 0,
  });

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadItem = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await fetch(`${BASE_URL}/recyclable/${id}`);
        if (!res.ok) {
          if (res.status === 404) throw new Error("Item not found.");
          throw new Error("Failed to load item.");
        }

        const data = await res.json();

        const next = {
          name: data?.name ?? "",
          image: data?.image ?? "",
          material: data?.material ?? "",
          quantity: Number(data?.quantity ?? 0),
        };

        if (!cancelled) setValues(next);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load item.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadItem();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const validate = () => {
    if (!values.name?.trim()) return "Item name cannot be empty.";
    if (!values.image?.trim()) return "Image URL cannot be empty.";
    if (!values.material?.trim()) return "Material cannot be empty.";
    if (Number.isNaN(Number(values.quantity)) || Number(values.quantity) < 0)
      return "Quantity must be 0 or more.";
    return "";
  };

  const handleUpdate = async () => {
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    setBusy(true);
    setError("");

    try {
      const payload = {
        name: values.name.trim(),
        image: values.image.trim(),
        material: values.material.trim(),
        quantity: Number(values.quantity),
      };

      const res = await fetch(`${BASE_URL}/recyclable/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to update item.");

      const result = await res.json().catch(() => ({}));
      alert(`✓ ${result.message || "Item updated successfully"}`);
      navigate("/allrecyclable");
    } catch (e) {
      setError(e.message || "Failed to update item. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    const ok = window.confirm("Delete this item? This cannot be undone.");
    if (!ok) return;

    setBusy(true);
    setError("");

    try {
      const res = await fetch(`${BASE_URL}/recyclable/${id}`, {
        method: "DELETE",
      });

      if (!res.ok) throw new Error("Failed to delete item.");

      const result = await res.json().catch(() => ({}));
      alert(`✓ ${result.message || "Item deleted successfully"}`);
      navigate("/allrecyclable");
    } catch (e) {
      setError(e.message || "Failed to delete item. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <main className="add-item-container">
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading item...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="add-item-container">
      {busy && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Saving...</p>
        </div>
      )}

      <div className="add-item-content">
        <div className="add-item-info">
          <div className="info-panel">
            <h3>🛠️ Edit Item</h3>
            <ul className="info-list">
              <li>
                <span className="info-icon">✏️</span>
                <span>Update details and save changes</span>
              </li>
              <li>
                <span className="info-icon">🗑️</span>
                <span>Delete removes the item permanently</span>
              </li>
            </ul>
          </div>

          <div className="preview-card">
            <div className="preview-header">ITEM PREVIEW</div>
            <div className="preview-content">
              {values.image ? (
                <img
                  src={values.image}
                  alt="Preview"
                  className="preview-image"
                  onError={(e) => {
                    e.target.src =
                      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect width='200' height='200' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='16' text-anchor='middle' fill='%23999'%3ENo Image%3C/text%3E%3C/svg%3E";
                  }}
                />
              ) : (
                <div className="preview-image empty">📷</div>
              )}

              <div className="preview-details">
                <h4>{values.name || "Item Name"}</h4>
                <p>
                  <strong>Material:</strong> {values.material || "Not selected"}
                </p>
                <p>
                  <strong>Quantity:</strong> {values.quantity || 0} units
                </p>
                {values.material && (
                  <div className="material-tag">{values.material}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="add-item-form-wrapper">
          <div className="form-header">
            <div className="form-icon">♻️</div>
            <h2>Edit Item</h2>
          </div>

          <ItemForm
            values={values}
            onChange={setValues}
            onSubmit={handleUpdate}
            busy={busy}
            error={error}
            submitText="Save Changes"
          />

          <div className="back-btn-container" style={{ display: "flex", gap: 12 }}>
            <button
              className="back-btn"
              onClick={() => navigate("/allrecyclable")}
              disabled={busy}
              style={{ flex: 1 }}
            >
              ← Back to Item List
            </button>

            <button
              className="back-btn"
              onClick={handleDelete}
              disabled={busy}
              style={{ flex: 1, borderColor: "#ff4d4f", color: "#ff4d4f" }}
            >
              Delete Item
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
